var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
import React, { Fragment, memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getClassName } from "../../helpers";
import { ENTITY_HEAD_CELL_PREFIX, ENTITY_TABLE, TABLE, TABLE_BODY_CELL, TABLE_BODY_CELL_SPAN, TABLE_HEAD_CELL, TABLE_HEAD_ROW, TEXT_ALIGN_CENTER, TEXT_ALIGN_LEFT, TEXT_ALIGN_RIGHT } from "../../classConstants";
import { Table } from "../../components/Table";
import styled from 'styled-components';
import { Loader } from "../../components/Loader/Loader";
import cancelToken from "../../cancelTokens";
import { Basic } from "../../components/Form";
import { useTranslation } from "react-i18next";
export var deriveAlignClassName = function (align) {
    var _a;
    if (align === void 0) { align = 'left'; }
    return (_a = {},
        _a[TEXT_ALIGN_RIGHT] = align === "right",
        _a[TEXT_ALIGN_LEFT] = align === "left",
        _a[TEXT_ALIGN_CENTER] = align === "center",
        _a);
};
var id = 0;
export var useFetchEntities = function (props) {
    var entityCancelToken = useState(function () { var _a; return cancelToken((_a = props.entityName) !== null && _a !== void 0 ? _a : ("" + ++id)); })[0];
    var t = useTranslation().t;
    useEffect(function () {
        return function () {
            entityCancelToken.cancel("Unmounted");
        };
    }, [entityCancelToken]);
    useEffect(function () {
        entityCancelToken.reset();
        props.fetchEntities(entityCancelToken).then(function () { entityCancelToken.reset(); });
        return function () {
            entityCancelToken.cancel("Updated");
        };
    }, [entityCancelToken, props.fetchEntities, props.entityName]);
    if (!props.hasInitialized || props.isLoading)
        return React.createElement(Loader, null);
    if (!props.records) {
        return React.createElement(React.Fragment, null);
    }
    if (!props.records.length) {
        return React.createElement(Basic, { as: "p" }, t("user_entity.no_data_to_display"));
    }
    return null;
};
var wrapWithSpan = (function (children) {
    return React.createElement("span", { className: TABLE_BODY_CELL_SPAN }, children);
});
export default function createEntityLayout(arg) {
    var _a;
    var entityName = arg.entityName, className = arg.className, getRecordClassName = arg.getRecordClassName, renderHeader = arg.renderHeader, EntityRow = arg.EntityRow, renderCell = arg.renderCell;
    var DefaultEntityRow = memo(function (props) {
        var columnHeaders = props.columnHeaders, data = props.data;
        return React.createElement("tr", null, columnHeaders.map(function (_a) {
            var _b;
            var key = _a.key, align = _a.align;
            return (React.createElement("td", { key: key, className: getClassName(TABLE_BODY_CELL, "ws-" + key.replace(/[A-Z]+(?![a-z])|[A-Z]/g, function ($, ofs) { return (ofs ? "-" : "") + $.toLowerCase(); }) + "-cell", deriveAlignClassName(align)) }, (_b = renderCell === null || renderCell === void 0 ? void 0 : renderCell(data, key, wrapWithSpan)) !== null && _b !== void 0 ? _b : wrapWithSpan(data[key])));
        }));
    });
    var ScreenHeader = (_a = arg.ScreenHeader) !== null && _a !== void 0 ? _a : Fragment;
    var RenderEntityRow = EntityRow !== null && EntityRow !== void 0 ? EntityRow : DefaultEntityRow;
    var Rows = memo(function (_a) {
        var records = _a.records, columnHeaders = _a.columnHeaders;
        var rows = records
            .map(function (datum, index) { return (React.createElement("tbody", { key: datum.id + '' + index, className: getClassName("ws-" + entityName + "-record-" + datum.id, getRecordClassName === null || getRecordClassName === void 0 ? void 0 : getRecordClassName(datum)) },
            React.createElement(RenderEntityRow, { columnHeaders: columnHeaders, data: datum, index: index, className: '', EntityRow: DefaultEntityRow }))); })
            .filter(Boolean);
        return React.createElement(React.Fragment, null, rows);
    });
    var ColGroup = function (props) {
        if (!props.shouldRender)
            return null;
        return (React.createElement("colgroup", null, props.columnHeaders.map(function (_a, i) {
            var key = _a.key;
            return (React.createElement("col", { key: key, className: "ws-colgroup-col-" + key, width: props.widthRef[i] }));
        })));
    };
    var Headers = memo(function (_a) {
        var columnHeaders = _a.columnHeaders, widthRef = _a.widthRef;
        var setHeaderWidth = function (index, width) {
            widthRef.current[index] = width;
        };
        var headers = columnHeaders.map(function (_a, i) {
            var _b;
            var label = _a.label, key = _a.key, align = _a.align;
            return (React.createElement("th", { key: key, ref: function (node) { var _a; return setHeaderWidth(i, (_a = node === null || node === void 0 ? void 0 : node.clientWidth) !== null && _a !== void 0 ? _a : 0); }, className: getClassName(TABLE_HEAD_CELL, ENTITY_HEAD_CELL_PREFIX + "-" + key, deriveAlignClassName(align)) }, (_b = renderHeader === null || renderHeader === void 0 ? void 0 : renderHeader(label, key)) !== null && _b !== void 0 ? _b : label));
        });
        return (React.createElement("thead", null,
            React.createElement("tr", { className: TABLE_HEAD_ROW }, headers)));
    });
    var EntityTable = (function (props) {
        var heading = props.heading;
        var loaderOrNull = useFetchEntities(props);
        var widthRef = useRef([]);
        var _a = useState([]), allWidths = _a[0], setWidths = _a[1];
        widthRef.current = useMemo(function () { return []; }, [props.records]);
        useLayoutEffect(function () {
            setWidths(widthRef.current.map(function (x) { return x + "px"; }));
        }, [props.records]);
        if (loaderOrNull)
            return loaderOrNull;
        var headers = React.createElement(Headers, { widthRef: widthRef, columnHeaders: props.columnHeaders });
        var rows = React.createElement(Rows, { columnHeaders: props.columnHeaders, records: props.records });
        var colGroups = React.createElement(ColGroup, { widthRef: allWidths, shouldRender: !!widthRef.current.length, columnHeaders: props.columnHeaders });
        var tableClass = getClassName(props.className, className || '', "ws-entity-" + entityName, ENTITY_TABLE, TABLE);
        return (React.createElement(React.Fragment, null, heading !== null && heading !== void 0 ? heading : React.createElement(ScreenHeader, null),
            React.createElement(OverflowScrollDiv, { className: "ws-entity-" + entityName + "-wrapper" },
                React.createElement(Table, { className: tableClass },
                    colGroups,
                    headers,
                    rows))));
    });
    var OverflowScrollDiv = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n        max-width: 100%;\n        overflow-x: auto;\n    "], ["\n        max-width: 100%;\n        overflow-x: auto;\n    "])));
    return styled(memo(EntityTable))(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n        td,\n        th {\n            &:not(:last-child) {\n                text-align: left;\n            }\n        }\n\n        td {\n            vertical-align: top;\n        }\n    "], ["\n        td,\n        th {\n            &:not(:last-child) {\n                text-align: left;\n            }\n        }\n\n        td {\n            vertical-align: top;\n        }\n    "])));
}
var templateObject_1, templateObject_2;
